import { AfterViewInit, Component, forwardRef, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { EMPTY, of, throwError } from 'rxjs';
import { DwRoutingService } from '@devwareapps/devware-cap';
import { environment } from '../../../../../environments/environment';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { CourseNavigationService } from '../../services/course-navigation.service';
import { ModuleVideoPlaybackService } from '../../../videos/services/module-video-playback.service';
import { ProgramTileCardComponent } from '../cards/program-tile-card/program-tile-card.component';
import { courseAuditFixture } from './break-ui-courses.fixtures';

@Component({
  selector: 'app-break-ui-courses',
  templateUrl: './break-ui-courses.component.html',
  styleUrls: ['../course-page/course-page.component.scss', './break-ui-courses.component.scss'],
  providers: [
    {provide: CourseRepositoryService, useExisting: forwardRef(() => BreakUiCoursesComponent)},
    {provide: CourseNavigationService, useExisting: forwardRef(() => BreakUiCoursesComponent)},
    {provide: DwRoutingService, useExisting: forwardRef(() => BreakUiCoursesComponent)},
    {provide: ModuleVideoPlaybackService, useValue: {preload: () => undefined}}
  ]
})
export class BreakUiCoursesComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(ProgramTileCardComponent) cards: QueryList<ProgramTileCardComponent>;
  readonly enabled = !environment.production && ['localhost', '127.0.0.1'].includes(location.hostname);
  readonly states = ['demo', 'worst', 'empty', 'one', 'huge', 'missing', 'loading', 'error', 'null'];
  readonly screens = ['list', 'course', 'module'];
  state = 'demo';
  screen = 'course';
  moduleId = 9000;
  mounted = true;
  progress = courseAuditFixture('demo');
  courses: any[] = [];
  notice = '';
  private cardSubscription;
  // ?retry=ok: only the first error/null read fails, so Retry can show recovery.
  private readonly retryOk = new URLSearchParams(location.search).get('retry') === 'ok';
  private reads = 0;
  constructor() {
    const params = new URLSearchParams(location.search);
    this.state = this.states.includes(params.get('data')) ? params.get('data') : 'demo';
    this.screen = this.screens.includes(params.get('screen')) ? params.get('screen') : 'course';
    this.moduleId = Number(params.get('module')) || 9000;
    this.prepare();
  }
  ngAfterViewInit(): void {
    this.cardSubscription = this.cards.changes.subscribe(() => this.configureCards());
    setTimeout(() => this.configureCards());
  }
  ngOnDestroy(): void { this.cardSubscription?.unsubscribe(); }
  selectState(state: string): void { this.state = state; this.moduleId = 9000; this.prepare(); this.remount(); }
  selectScreen(screen: string): void { this.screen = screen; this.remount(); }
  getRoutesChanges() { return EMPTY; }
  getCourseProgress() {
    if (this.state === 'loading') { return EMPTY; }
    const fail = !this.retryOk || this.reads++ === 0;
    if (this.state === 'error' && fail) { return throwError(new Error('Synthetic course read failure')); }
    if (this.state === 'null' && fail) { return of(null); }
    if (this.state === 'empty' && this.screen === 'module') {
      const emptyModule = courseAuditFixture('one');
      emptyModule.modules[0].lessons = [];
      emptyModule.modules[0].totalLessons = 0;
      emptyModule.modules[0].completedLessons = 0;
      emptyModule.totalLessons = 0;
      emptyModule.completedLessons = 0;
      emptyModule.modules[0].moduleProgress.PercentComplete = 0;
      emptyModule.modules[0].module.TotalModuleTimeSeconds = 0;
      emptyModule.percentComplete = 0;
      return of(emptyModule);
    }
    return of(this.progress);
  }
  getCourseAvailabilityNotifications() { return of([]); }
  getCurrentCourseParameters() { return {courseId: 900, moduleId: this.moduleId}; }
  navigateToCourse() { this.selectScreen('course'); }
  navigateToModule(courseId: number, moduleId: number) { this.moduleId = moduleId; this.selectScreen('module'); }
  navigateToLesson() { this.notice = 'Lesson target reached; player/progress writes intentionally excluded from this audit.'; }
  resumeCourse() { this.navigateToLesson(); }
  private prepare(): void {
    this.progress = courseAuditFixture(this.state);
    const count = this.state === 'empty' ? 0 : this.state === 'one' ? 1 : this.state === 'huge' ? 1000 : 4;
    this.courses = Array.from({length: count}, (_, i) => ({CourseId: 900 + i,
      CourseName: this.state === 'missing' ? ['', ' ', undefined, 'J'][i % 4] : this.state === 'worst' || this.state === 'huge'
        ? this.progress.modules[i % this.progress.modules.length].module.ModuleName : 'Private Pilot Airplane',
      StudentCoursePercentComplete: this.state === 'worst' ? [142, -3, 0, 100][i % 4] : 25,
      StudentCourseModulesCompleted: this.state === 'missing' ? undefined : this.state === 'worst' ? 1284 : 0,
      StudentCourseModulesCount: this.state === 'one' ? 1 : this.state === 'worst' ? 1285 : 23, MediaMediaPath: ''}));
  }
  private remount(): void {
    this.mounted = false;
    this.reads = 0;
    this.notice = '';
    const url = new URL(location.href);
    url.searchParams.set('data', this.state); url.searchParams.set('screen', this.screen); url.searchParams.set('module', String(this.moduleId));
    history.replaceState(null, '', url.toString());
    setTimeout(() => { this.mounted = true; setTimeout(() => this.configureCards()); });
  }
  private configureCards(): void {
    this.cards?.forEach((card, i) => {
      card.data = this.courses[i]; card.setupCard();
      if (this.state === 'worst' && i === 0) card.courseImageUrl = '/assets/course-audit-missing.test.png';
    });
  }
}
